import { useEffect, useMemo, useState } from "react";
import { BookOpen, ChevronRight, Lock, Search, ShieldCheck } from "lucide-react";
import { useStore } from "@/provider/store";
import { useMetricDefinitions } from "@/components/daylight-insight/api/useMetricDefinitions";
import type {
  MetricDefinition,
  MetricDefinitionDashboard,
} from "@/components/daylight-insight/api/types";
import "@/components/skeleton/dashboard-skeleton.css";

/** `MetricCalc` → the unit chip next to a metric's name. The SP's vocabulary is
 *  small and fixed; anything unrecognised is shown as it came so a newly added
 *  calc type is never silently dropped. */
const CALC_LABEL: Record<string, string> = {
  count: "Count",
  sum: "Total",
  percent: "Percent",
  average: "Average",
  averagedip: "Avg per day",
};
const calcLabel = (calc: string | null): string =>
  calc ? CALC_LABEL[calc.toLowerCase()] ?? calc : "";

/** A dashboard's drill grain reads "Drill by Patient · Episode" in the SP but
 *  the chips supply their own "Drill by" / "Drill:" prefix. */
const grainText = (grain: string | null): string =>
  (grain || "").replace(/^drill\s+by\s+/i, "").trim();

/** The one description line: the long form when authored, else the short one.
 *  They are frequently the same string, and the short form is often just the
 *  metric name — so only one is ever shown. */
const descOf = (m: MetricDefinition): string | null =>
  m.longDesc || (m.shortDesc && m.shortDesc !== m.name ? m.shortDesc : null);

/** The formula, unless it merely repeats "why it matters" — several rows in the
 *  dictionary have the same sentence in both columns, and rendering it twice
 *  (once as prose, once as a code block) reads like a data error. */
const formulaOf = (m: MetricDefinition): string | null =>
  m.formula && m.formula !== m.whyItMatters ? m.formula : null;

const matches = (m: MetricDefinition, q: string): boolean =>
  [m.name, m.code, m.shortDesc, m.longDesc, m.whyItMatters, m.formula]
    .some((v) => (v || "").toLowerCase().includes(q));

/** Shimmer placeholder for the dashboard cards, up while the definitions call
 *  is in flight — the guide has no cached copy to show in the meantime. */
function GuideSkeleton() {
  return (
    <>
      {[0, 1, 2].map((i) => (
        <div className="pillar" key={i} aria-hidden="true">
          <div className="pillar-head" style={{ cursor: "default" }}>
            <span className="sk" style={{ width: 38, height: 38, borderRadius: 10 }} />
            <div style={{ flex: 1 }}>
              <span className="sk" style={{ width: 150, height: 13, borderRadius: 6, display: "block" }} />
              <span className="sk" style={{ width: 300, height: 11, borderRadius: 6, display: "block", marginTop: 7 }} />
            </div>
            <span className="sk" style={{ width: 86, height: 20, borderRadius: 20 }} />
          </div>
        </div>
      ))}
    </>
  );
}

/**
 * Metrics Guide — the data dictionary for the service line on screen, served by
 * `[DayLight].[GetMetricDefinitions]`: every dashboard, its metrics, and each
 * metric's plain-English definition, why it matters, how it's calculated, how
 * it drills down and what it relates to.
 *
 * The dashboards come from the SP; which of them this user may OPEN is still
 * the role's grants from `dashboard/config` (`store.dashboards`), so a
 * dashboard the selected role lacks renders locked rather than disappearing —
 * the guide is also how someone learns what they don't have access to.
 *
 * Benchmarks, targets and thresholds are deliberately absent: the definitions
 * SP returns no such columns, so there is nothing to show for them.
 */
export function GuideViewInsight() {
  const s = useStore();
  const { serviceLineSystemID, dashboards: grants, allowedPillars, roleName } = s;
  const { data, loading, error } = useMetricDefinitions(serviceLineSystemID);
  // The definitions call can't fire until the config resolves the service line,
  // and a disabled query reports `loading: false` — so that window counts as
  // loading here, otherwise the guide flashes its empty state on every open.
  const pending = loading || !serviceLineSystemID;

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const q = query.trim().toLowerCase();

  const pillars: MetricDefinitionDashboard[] = useMemo(
    () => data?.dashboards ?? [],
    [data],
  );

  /** The dashboard ids the selected role may open, or null while the grants are
   *  unknown (config still in flight / failed) — which gates nothing, matching
   *  `allowedPillars`' own degrade-open contract. */
  const grantedIds = useMemo(
    () =>
      allowedPillars === null
        ? null
        : new Set(grants.map((g) => String(g.dashboardId))),
    [grants, allowedPillars],
  );
  const canSee = (d: MetricDefinitionDashboard) =>
    grantedIds === null || grantedIds.has(String(d.dashboardId));

  const keyOf = (d: MetricDefinitionDashboard) => String(d.dashboardId ?? d.name);

  // The first dashboard the user can actually open starts expanded. Resolved in
  // an effect because the list arrives with the API response, not on mount —
  // and only while nothing is expanded yet, so it can't reopen a card the user
  // has just collapsed.
  useEffect(() => {
    const first = pillars.find(canSee);
    if (!first) return;
    setOpen((prev) => (prev.size ? prev : new Set([keyOf(first)])));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pillars, grantedIds]);

  const total = pillars.length;
  const accessibleCount = pillars.filter(canSee).length;

  const toggle = (k: string) =>
    setOpen((prev) => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });

  return (
    <>
      <div className="guide-hero">
        <div className="guide-eyebrow">Metrics Guide</div>
        <h1 className="guide-title">Know every number before you read it.</h1>
        <div className="guide-sub">Plain-English definitions for each metric you can access, grouped by dashboard — what it measures, how it's calculated, why it matters, and how it drills down. Your role determines which dashboards you see.</div>
      </div>

      <div className="guide-bar">
        <div className="guide-search">
          <Search size={15} />
          <input placeholder="Search metrics…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
        <div style={{ flex: 1 }} />
        <span className="guide-access"><ShieldCheck size={15} style={{ color: "var(--green)" }} />{roleName ? `${roleName} · ` : ""}access to {accessibleCount} of {total} dashboard{total === 1 ? "" : "s"}</span>
      </div>

      <div className="guide-body">
        {pending && !data ? (
          <GuideSkeleton />
        ) : error && !pillars.length ? (
          <div className="pillar" style={{ padding: 22, textAlign: "center", color: "var(--ink-3)" }}>{error}</div>
        ) : !pillars.length ? (
          <div className="pillar" style={{ padding: 22, textAlign: "center", color: "var(--ink-3)" }}>No metric definitions have been published for this service line yet.</div>
        ) : (
          pillars.map((p) => {
            const key = keyOf(p);
            const allowed = canSee(p);
            const shown = q ? p.metrics.filter((m) => matches(m, q)) : p.metrics;
            if (q && (!allowed || shown.length === 0)) return null;
            const isOpen = q ? true : open.has(key);

            if (!allowed) {
              return (
                <div key={key} className="pillar locked">
                  <div className="pillar-head">
                    <span className="pillar-mark"><Lock size={17} /></span>
                    <div style={{ flex: 1 }}>
                      <div className="pillar-name">{p.name}</div>
                      <div className="pillar-blurb">Not available for the {roleName || "selected"} role.</div>
                    </div>
                    <div className="pillar-meta"><span className="pillar-count">Restricted</span></div>
                  </div>
                </div>
              );
            }
            return (
              <div key={key} className={"pillar" + (isOpen ? " open" : "")}>
                <div className="pillar-head" onClick={() => !q && toggle(key)}>
                  <span className="pillar-mark"><BookOpen size={17} /></span>
                  <div style={{ flex: 1 }}>
                    <div className="pillar-name">{p.name}</div>
                    {p.description && <div className="pillar-blurb">{p.description}</div>}
                  </div>
                  <div className="pillar-meta">
                    {p.drillGrain && <span className="pillar-grain">Drill by {grainText(p.drillGrain)}</span>}
                    <span className="pillar-count">{shown.length} metric{shown.length === 1 ? "" : "s"}</span>
                    {!q && <ChevronRight size={16} style={{ color: "var(--ink-3)", transform: isOpen ? "rotate(90deg)" : "none", transition: ".15s" }} />}
                  </div>
                </div>
                {isOpen && (
                  <div className="pillar-body">
                    {shown.map((m) => {
                      const desc = descOf(m);
                      const formula = formulaOf(m);
                      const unit = calcLabel(m.calc);
                      const grain = grainText(m.drillGrain || p.drillGrain);
                      return (
                        <div className="def-row" key={m.code}>
                          <div className="def-main">
                            <div className="def-name">{m.name}{unit && <span className="def-unit">{unit}</span>}</div>
                            {desc && <div className="def-text">{desc}</div>}
                            {m.whyItMatters && <div className="def-why">{m.whyItMatters}</div>}
                            {formula && <div className="def-formula">{formula}</div>}
                            {m.relations.length > 0 && (
                              <div className="def-rels">
                                {m.relations.map((r) => (
                                  <span className="def-rel" key={`${r.relationId ?? r.metricCode}`}>
                                    {r.relationship ? `${r.relationship.toLowerCase()} ` : ""}{r.metricName}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="def-side">
                            {grain && <span className="pillar-grain">Drill: {grain}</span>}
                          </div>
                        </div>
                      );
                    })}
                    {shown.length === 0 && <div className="def-locknote">No metrics are defined for this dashboard yet.</div>}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </>
  );
}
